// Store-group row shaping for the batch grid.
//
// A store search returns one flat row per (store, item). The grid shows one row per item
// for the focal store and hangs the other stores' rows off it as `alternate_store_items`,
// which the expanded cascaded table renders. When the focal store has no row for an item,
// the highest-priority member store is promoted into that position instead.
//
// This lives outside BatchView.getItems so the grouping can be tested on plain arrays:
// getItems also does request sequencing, paging, banner extraction, auto-exclusion and
// client sorting, and none of that is needed to exercise the shaping.

// Latest timestamp across a row and the other stores' rows for the same item.
export function maxUpdatedOn(item, alternates = []) {
  const timestamps = [item.updated_on, ...alternates.map(a => a.updated_on)].filter(Boolean)
  if (timestamps.length === 0) return item.updated_on
  return timestamps.reduce((max, ts) => (ts > max ? ts : max))
}

// Applies any pending edit to a cascaded row. Focal rows get this treatment where they are
// built; without it here, an unsaved child edit vanishes on reload while its entry survives
// in `changes` and localStorage.
function withPendingChange(row, changes) {
  const change = changes.find(c => c.id === row.id)
  return change ? { ...row, ...change } : row
}

function byPriority(a, b) {
  return Number(a.priority) - Number(b.priority)
}

/**
 * Mutates and returns `updatedItem`, marking it as the focal row, the promoted member row,
 * or neither, and attaching the other stores' rows.
 *
 * @param {object} item          the raw API row this grid row was built from
 * @param {object} updatedItem   the row being shaped (mutated)
 * @param {object} opts
 * @param {Map}    opts.rowsByItemId       every raw row, grouped by item_id
 * @param {string} opts.primaryStoreId     the focal store's party id
 * @param {string[]} opts.storeGroupPartyIds  every store in the selected group
 * @param {object[]} opts.changes          pending unsaved edits
 * @param {Function} opts.hasRules         predicate over a row's rules_applied
 */
export function attachStoreRows(item, updatedItem, opts) {
  const {
    rowsByItemId,
    primaryStoreId,
    storeGroupPartyIds = [],
    changes = [],
    hasRules = () => false
  } = opts

  const sameItemRows = rowsByItemId.get(item.item_id) || []
  const isPrimaryStoreItem = item.store_id === primaryStoreId
  updatedItem.is_primary_store_item = isPrimaryStoreItem

  if (isPrimaryStoreItem) {
    const alternateStoreItems = sameItemRows
      .filter(altItem => altItem.store_id !== item.store_id)
      .map(altItem => withPendingChange(altItem, changes))
    updatedItem.alternate_store_items = alternateStoreItems.sort(byPriority)
    updatedItem.alternate_store_items.forEach(asi => {
      asi.comp_pricing_mode = updatedItem.comp_pricing_mode
    })
    updatedItem.updated_on = maxUpdatedOn(item, alternateStoreItems)
    return updatedItem
  }

  // No focal row for this item: the highest-priority member store stands in for it, but
  // only if the engine actually priced it.
  const primaryStoreItem = sameItemRows.some(altItem => altItem.store_id === primaryStoreId)
  if (primaryStoreItem) return updatedItem

  const otherMatchingItems = sameItemRows.filter(
    altItem => item.store_id !== altItem.store_id && altItem.store_id !== primaryStoreId
  )
  const priorityOrderedItems = otherMatchingItems.sort(byPriority)
  const isHighestPriorityAltItem = priorityOrderedItems.length > 0
    ? item.priority < priorityOrderedItems[0].priority
    : false

  if (!isHighestPriorityAltItem && otherMatchingItems.length !== 0) return updatedItem

  const isAltStoreItem = storeGroupPartyIds.includes(item.store_id)
  if (isAltStoreItem && hasRules(item.rules_applied)) {
    updatedItem.is_alt_store_item = isAltStoreItem
    updatedItem.alternate_store_items = priorityOrderedItems.map(
      altItem => withPendingChange(altItem, changes)
    )
    updatedItem.updated_on = maxUpdatedOn(item, priorityOrderedItems)
  }
  return updatedItem
}
